import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { getRememberMePreference, setRememberMePreference } from "../session-policy";

const CHANNEL = "bac:web-auth:live-tabs:v1";
const REVISION = "bac:web-auth:revision";
const TAB_REVISION = "bac:web-auth:tab-revision";
const WAIT_MS = 600;
const VALIDATE_MS = 4000;
let remoteResetting = false;
export function isRemoteTabResetting() { return remoteResetting; }
type Tokens = Pick<Session, "access_token" | "refresh_token">;
export function sessionRevision(session: Pick<Session, "access_token">): string | null {
  try {
    const payload = JSON.parse(atob(session.access_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.session_id === "string" ? payload.session_id : null;
  } catch { return null; }
}
type Message =
  | { kind: "request"; id: string; revision: string }
  | { kind: "offer"; id: string; revision: string; tokens: Tokens; remember: boolean }
  | { kind: "invalidate"; revision: string }
  | { kind: "available"; revision: string };

function revision(): string | null {
  try { return localStorage.getItem(REVISION) || ""; } catch { return null; }
}

/** Reconcile dormant tabs before Supabase can read their old tab-local tokens.
 * Only opaque invalidation metadata is durable; credentials never travel here.
 */
export function reconcileTabRevision(storageKey: string): void {
  try {
    const current = revision();
    if (current !== null && current !== (sessionStorage.getItem(TAB_REVISION) || "")) {
      sessionStorage.removeItem(storageKey);
    }
    sessionStorage.setItem(TAB_REVISION, current || "");
  } catch { /* Restricted storage: live handoff will be disabled. */ }
}

export class LiveTabSessions {
  private channel: BroadcastChannel | null = null;
  private current: Session | null = null;
  private generation = 0;
  private receiving = false;
  private pending: { id: string; revision: string; finish: (m: Message | null) => void } | null = null;
  private restoreFlight: Promise<Session | null> | null = null;
  private blocked = false;
  private ownRevision = revision();
  private resetFlight: Promise<void> = Promise.resolve();
  private remember: boolean;
  private abandonedRevision: string | null = null;

  constructor(private client: SupabaseClient, private readPersistence = getRememberMePreference) {
    this.remember = readPersistence();
    // auth-js broadcasts unvalidated sessions straight to subscribers but does
    // not persist them in sessionStorage. Own this transport instead. This is
    // the sole SDK-internal bridge; the transport regression pins this bridge.
    const auth = client.auth as typeof client.auth & { broadcastChannel?: BroadcastChannel | null };
    auth.broadcastChannel?.close();
    auth.broadcastChannel = null;
    try {
      if (this.ownRevision !== null && typeof BroadcastChannel !== "undefined") {
        sessionStorage.setItem(TAB_REVISION, this.ownRevision);
        this.channel = new BroadcastChannel(CHANNEL);
        this.channel.onmessage = (event: MessageEvent<Message>) => {
          void this.receive(event.data).catch(() => { /* Untrusted/malformed messages fail closed. */ });
        };
      }
    } catch { this.channel = null; }
    client.auth.onAuthStateChange((event, session) => {
      if (this.receiving) return;
      if (event === "SIGNED_OUT") {
        this.invalidate();
      } else if (session) {
        // Recovered SIGNED_IN and token rotation keep the same server session
        // id. Only an actual new login changes browser identity.
        const nextRevision = sessionRevision(session);
        if (nextRevision && nextRevision === this.abandonedRevision) return;
        const explicitLogin = event === "SIGNED_IN" || event === "PASSWORD_RECOVERY";
        if (!explicitLogin && nextRevision && this.ownRevision &&
          nextRevision !== revision()) {
          this.resetFlight = this.dropRemote();
          return;
        }
        if (explicitLogin && nextRevision && nextRevision !== this.ownRevision) {
          this.generation++;
          this.pending?.finish(null);
          this.ownRevision = nextRevision;
          try { localStorage.setItem(REVISION, nextRevision); } catch { this.channel?.close(); this.channel = null; }
          this.post({ kind: "invalidate", revision: nextRevision });
        }
        this.blocked = false;
        this.current = session;
        this.remember = this.readPersistence();
        this.stamp();
        if (event !== "INITIAL_SESSION") this.post({ kind: "available", revision: this.ownRevision || "" });
      }
    });
    // A storage event contains no credentials and also reaches tabs whose
    // BroadcastChannel is unavailable. Never let those tabs resurrect identity.
    if (typeof window !== "undefined") {
      window.addEventListener("storage", event => {
        if (event.key === REVISION && revision() !== this.ownRevision) this.resetFlight = this.dropRemote();
      });
    }
  }

  accepts(session: Session): boolean {
    const active = revision();
    const candidate = sessionRevision(session);
    if (candidate && candidate === this.abandonedRevision) return false;
    return active === null || !active || !candidate || candidate === active;
  }

  private stamp() {
    try { sessionStorage.setItem(TAB_REVISION, this.ownRevision || ""); } catch { /* noop */ }
  }

  private post(message: Message) {
    try { this.channel?.postMessage(message); } catch { /* fail closed */ }
  }

  /** Call synchronously before explicit logout clears storage or navigates. */
  invalidate() {
    this.abandonedRevision = this.current ? sessionRevision(this.current) : this.abandonedRevision;
    this.generation++;
    this.pending?.finish(null);
    this.current = null;
    this.blocked = true;
    try {
      this.ownRevision = crypto.randomUUID();
      localStorage.setItem(REVISION, this.ownRevision);
      this.stamp();
      this.post({ kind: "invalidate", revision: this.ownRevision });
    } catch { this.channel?.close(); this.channel = null; }
  }

  private async dropRemote() {
    this.abandonedRevision = this.current ? sessionRevision(this.current) : this.abandonedRevision || this.ownRevision;
    this.generation++;
    this.pending?.finish(null);
    this.current = null;
    this.blocked = true;
    this.ownRevision = revision();
    this.stamp();
    // Do not revoke other devices or echo another invalidation.
    this.receiving = true;
    remoteResetting = true;
    try { await this.client.auth.signOut({ scope: "local" }); }
    finally { this.receiving = false; remoteResetting = false; }
  }

  private async receive(message: Message) {
    if (!message || typeof message !== "object" || typeof message.revision !== "string") return;
    if (message.kind === "invalidate") {
      if (revision() !== this.ownRevision) await (this.resetFlight = this.dropRemote());
      return;
    }
    if (message.revision !== revision() || message.revision !== this.ownRevision) return;
    if (message.kind === "request" && typeof message.id === "string") {
      const s = this.current;
      if (!s || this.blocked || !s.expires_at || s.expires_at * 1000 <= Date.now() + 5000) return;
      this.post({
        kind: "offer", id: message.id, revision: message.revision,
        tokens: { access_token: s.access_token, refresh_token: s.refresh_token },
        remember: this.remember,
      });
    } else if (message.kind === "offer" && message.id === this.pending?.id) {
      this.pending.finish(message);
    } else if (message.kind === "available") {
      await this.resetFlight;
      if (message.revision !== revision()) return;
      this.blocked = false;
      // Includes a listing opened before login, and rotated credentials.
      await this.restoreFlight;
      void this.restore(true);
    }
  }

  restore(refresh = false): Promise<Session | null> {
    if (this.restoreFlight) return this.restoreFlight;
    this.restoreFlight = this.performRestore(refresh).finally(() => { this.restoreFlight = null; });
    return this.restoreFlight;
  }

  private async performRestore(refresh: boolean): Promise<Session | null> {
    const start = this.generation;
    if (!this.channel || this.blocked || this.ownRevision === null ||
      this.ownRevision === this.abandonedRevision) return null;
    // Callback/recovery URL sessions have authority over a live-tab offer.
    if (typeof location !== "undefined" &&
      (/[#&](access_token|refresh_token|type)=/.test(location.hash) ||
        new URLSearchParams(location.search).has("code") || /\/auth\/callback|\/set-password/.test(location.pathname))) return null;
    const response = await new Promise<Message | null>(resolve => {
      const id = crypto.randomUUID();
      const timer = setTimeout(() => finish(null), WAIT_MS);
      const finish = (message: Message | null) => {
        clearTimeout(timer);
        this.pending = null;
        resolve(message);
      };
      this.pending = { id, revision: this.ownRevision!, finish };
      this.post({ kind: "request", id, revision: this.ownRevision! });
    });
    if (response?.kind !== "offer" || typeof response.remember !== "boolean" ||
      typeof response.tokens?.access_token !== "string" || typeof response.tokens.refresh_token !== "string") return null;
    if (response.revision && sessionRevision(response.tokens) !== response.revision) return null;
    const valid = () => start === this.generation && response.revision === revision() && !this.blocked;
    if (!valid()) return null;
    // getUser(jwt) asks the auth server, rather than trusting a decoded JWT or
    // the peer's user object. Bound it without adopting a late response.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const verified = await Promise.race([
      this.client.auth.getUser(response.tokens.access_token).catch(() => null),
      new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), VALIDATE_MS); }),
    ]).finally(() => clearTimeout(timer));
    if (!verified?.data.user || verified.error || !valid()) return null;
    if (this.current && this.current.user.id !== verified.data.user.id) return null;
    if (!refresh && this.current) return this.current;
    setRememberMePreference(response.remember);
    const initialAdoption = !this.current;
    this.receiving = true;
    try {
      // A new tab exchanges the refresh token too: an unexpired JWT alone
      // does not prove that the server session has not been revoked.
      const adoption = initialAdoption
        ? this.client.auth.refreshSession(response.tokens)
        : this.client.auth.setSession(response.tokens);
      let adoptionTimer: ReturnType<typeof setTimeout> | undefined;
      const result = await Promise.race([
        adoption,
        new Promise<null>(resolve => { adoptionTimer = setTimeout(() => resolve(null), VALIDATE_MS); }),
      ]).finally(() => clearTimeout(adoptionTimer));
      if (!result) {
        this.generation++;
        this.blocked = true;
        this.abandonedRevision = response.revision;
        // A late SDK response must not resurrect an abandoned adoption.
        void adoption.then(() => this.dropRemote()).catch(() => {});
        return null;
      }
      const { data, error } = result;
      if (!valid()) {
        await this.dropRemote();
        return null;
      }
      if (error || data.session?.user.id !== verified.data.user.id) return null;
      this.current = data.session;
      this.remember = response.remember;
      this.stamp();
      if (initialAdoption) this.post({ kind: "available", revision: this.ownRevision! });
      return data.session;
    } finally { this.receiving = false; }
  }
}
